import { useEffect, useState } from "react";
import { FaEye, FaHistory, FaInbox, FaSpinner } from "react-icons/fa";
import { Users, LogOut, Stethoscope, HeartPulse } from "lucide-react";
import Layouts from "../../Layouts";
import IpdService from "../../../Services/IpdService";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { APP_ROUTES } from "../../../PageRouting/approutes";
import CareTeamModal from "./CareTeamModal";
import HimsModuleCards from "../NewDashboard/HimsModuleCards";
import dayjs from "dayjs";

const STATUS_STYLE = {
  ADMITTED: "bg-blue-50 text-blue-600 before:bg-blue-500",
  CRITICAL: "bg-red-50 text-red-600 before:bg-red-500",
  UNDER_TREATMENT: "bg-yellow-50 text-yellow-600 before:bg-yellow-500",
  DISCHARGED: "bg-emerald-50 text-emerald-600 before:bg-emerald-500",
};

const STAT_CARDS = [
  {
    key: "totalIpd",
    altKey: "totalAssigned",
    label: "Total Assigned patients",
    sub: "All Time",
    icon: Users,
    color: "bg-teal-50 text-teal-600",
  },
  {
    key: "discharged",
    altKey: "todaysDischarge",
    label: "Todays Discharge",
    sub: "Today",
    icon: LogOut,
    color: "bg-emerald-50 text-emerald-600",
  },
  {
    key: "occupiedBeds",
    altKey: "todaysRounds",
    label: "Occupied Beds",
    sub: "Currently",
    icon: Stethoscope,
    color: "bg-orange-50 text-orange-600",
  },
  {
    key: "criticalCare",
    altKey: "criticalPatients",
    label: "Critical Patients",
    sub: "Currently",
    icon: HeartPulse,
    color: "bg-red-50 text-red-600",
  },
];

const COLUMNS = [
  { key: "admissionNo", label: "Admission No" },
  { key: "patientId", label: "Patient ID" },
  { key: "doctorId", label: "Doctor ID" },
  { key: "departmentId", label: "Dept ID" },
  { key: "wardCode", label: "Ward" },
  { key: "bedNumber", label: "Bed" },
  { key: "admissionDate", label: "Admission Date" },
  { key: "diagnosis", label: "Diagnosis" },
  { key: "admissionStatus", label: "Status" },
  { key: "assignedDoctorId", label: "Assigned Doctor" },
  { key: "expectedDischargeDate", label: "Expected Discharge" },
];

const SkeletonRow = () => (
  <tr className="border-b border-slate-100 animate-pulse">
    {COLUMNS.map((_, i) => (
      <td key={i} className="px-5 py-4">
        <div className="h-3 bg-slate-200 rounded w-3/4" />
      </td>
    ))}
  </tr>
);

export default function Index() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedAssignmentPatient, setSelectedAssignmentPatient] =
    useState(null);
  const pageSize = 10;

  const storedLogin = sessionStorage.getItem("login_result");
  const user = storedLogin ? JSON.parse(storedLogin)?.user : null;

  const [counts, setCounts] = useState({
    totalIpd: 0,
    occupiedBeds: 0,
    criticalCare: 0,
    discharged: 0,
    totalExpectedDischargeCount: 0,
    totalDischargedCount: 0,
    totalAssigned: 0,
    todaysDischarge: 0,
    todaysRounds: 0,
    criticalPatients: 0,
  });
  const [countsLoading, setCountsLoading] = useState(false);

  const fetchCounts = async () => {
    const doctorId = user?.userId;
    if (!doctorId) return;
    setCountsLoading(true);
    try {
      const payload = { doctorId };
      const res = await IpdService.getInPatientCount(payload);
      const data = res?.data?.data || res?.data || {};

      setCounts({
        totalIpd: data.totalIpd ?? 0,
        occupiedBeds: data.occupiedBeds ?? 0,
        criticalCare: data.criticalCare ?? 0,
        discharged: data.discharged ?? 0,
        totalExpectedDischargeCount: data.totalExpectedDischargeCount ?? 0,
        totalDischargedCount: data.totalDischargedCount ?? 0,

        // Alt keys for cards
        totalAssigned: data.totalIpd ?? 0,
        todaysDischarge: data.discharged ?? 0,
        todaysRounds: data.occupiedBeds ?? 0,
        criticalPatients: data.criticalCare ?? 0,
      });
    } catch (err) {
      console.error("Failed to fetch inpatient counts:", err);
    } finally {
      setCountsLoading(false);
    }
  };

  useEffect(() => {
    fetchCounts();
  }, [user?.userId]);

  const fetchList = async (pageNo) => {
    setLoading(true);
    try {
      const res = await IpdService.getIpdList({
        doctorId: user?.userId,
        pageNo,
        pageSize,
      });
      setList(res?.data?.admissionList ?? []);
    } catch (err) {
      console.error("Failed to fetch IPD list:", err);
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList(currentPage);
  }, [currentPage]);

  const history = useHistory();
  const handleViewClick = (admission) => {
    history.push(
      APP_ROUTES.INPATIENTDETAIL.replace(":id", admission?.patientId),
      { admission },
    );
  };

  const handlePrev = () => setCurrentPage((p) => Math.max(p - 1, 1));
  const handleNext = () => {
    if (list.length === pageSize) setCurrentPage((p) => p + 1);
  };

  return (
    <Layouts>
      <div className="min-h-full px-4 py-6 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-[1400px] space-y-6">
          {/* ── Common HIMS Module Cards ── */}
          <HimsModuleCards
            activeId="wards"
            counts={{
              wards_critical:
                counts?.criticalPatients ?? counts?.criticalCare ?? 0,
              wards_stable: counts?.occupiedBeds ?? 0,
            }}
          />

          <h1 className="text-2xl font-semibold text-slate-900">In Patients</h1>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {STAT_CARDS.map(
              ({ key, altKey, label, sub, icon: Icon, color }) => (
                <div
                  key={key}
                  className="h-28 rounded-xl border border-brand-colorBorder bg-white p-3 shadow-lg"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex gap-2.5 items-center justify-center">
                      <div
                        className={`h-11 w-11 flex items-center justify-center rounded-lg ${color}`}
                      >
                        <Icon size={20} />
                      </div>
                      <p className="text-sm font-medium text-slate-800">
                        {label}
                      </p>
                    </div>

                    <div className="text-right">
                      <p className="text-2xl font-semibold text-slate-800">
                        {countsLoading ? (
                          <FaSpinner className="inline animate-spin text-base text-slate-400" />
                        ) : (
                          (counts[key] ?? (altKey ? counts[altKey] : 0) ?? 0)
                        )}
                      </p>
                      <p className="text-xs text-slate-400">{sub}</p>
                    </div>
                  </div>
                </div>
              ),
            )}
          </div>

          {/* Table */}
          <div className="overflow-auto rounded-lg border border-slate-200">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-[#F1FFF9] text-slate-700 border-b text-sm whitespace-nowrap">
                  <th className="px-5 py-3 font-semibold">Patient</th>
                  <th className="px-5 py-3 font-semibold">Admission No</th>
                  <th className="px-5 py-3 font-semibold">Age / Gender</th>
                  <th className="px-5 py-3 font-semibold">Ward</th>
                  <th className="px-5 py-3 font-semibold">Bed</th>
                  <th className="px-5 py-3 font-semibold">Diagnosis</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                  <th className="px-5 py-3 font-semibold">Admitted On</th>

                  <th className="px-5 py-3 font-semibold text-center">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading &&
                  Array.from({ length: 5 }).map((_, i) => (
                    <SkeletonRow key={i} cols={11} />
                  ))}

                {!loading && list.length === 0 && (
                  <tr>
                    <td
                      colSpan={COLUMNS.length}
                      className="py-16 text-center text-slate-400"
                    >
                      <FaInbox className="text-4xl mx-auto mb-3 opacity-30" />
                      <p className="text-sm font-medium">
                        No IPD admissions found.
                      </p>
                    </td>
                  </tr>
                )}

                {!loading &&
                  list.map((item, index) => (
                    <tr
                      key={item.admissionId ?? index}
                      className="border-b border-slate-100 text-sm text-slate-700 hover:bg-slate-50 transition-colors whitespace-nowrap"
                    >
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <div>
                            <p className="font-semibold text-slate-800">
                              {item.patientName || "—"}
                            </p>
                            <p className="text-xs text-slate-400">
                              {item.patientId || ""}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3 font-bold text-indigo-600">
                        {item.admissionNo || "—"}
                      </td>
                      <td className="px-5 py-3">
                        {item.age ?? "—"} / {item.gender ?? "—"}
                      </td>
                      <td className="px-5 py-3">{item.wardName || "—"}</td>
                      <td className="px-5 py-3 font-semibold">
                        {item.bedNumber || "—"}
                      </td>
                      <td className="px-5 py-3">{item.diagnosis || "—"}</td>
                      <td className="px-5 py-3">
                        <span
                          className={`relative inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold
                            before:content-[''] before:w-2 before:h-2 before:rounded-full`}
                        >
                          {item?.admissionStatus || "—"}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        {" "}
                        {dayjs(item?.admissionDate).format("DD-MM-YYYY hh:mm")}
                      </td>

                      <td className="px-5 py-3">
                        <div className="flex justify-center items-center gap-2">
                          <button
                            onClick={() => handleViewClick(item)}
                            className="px-3 py-1.5 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-600 text-xs font-bold flex items-center gap-1.5 hover:bg-indigo-100 transition-colors"
                          >
                            <FaEye className="text-xs" />
                            View
                          </button>
                          <button
                            onClick={() => setSelectedAssignmentPatient(item)}
                            className="px-3 py-1.5 rounded-lg border border-slate-200 bg-slate-50 text-slate-600 text-xs font-bold flex items-center gap-1.5 hover:bg-slate-100 hover:text-slate-900 transition-colors"
                            title="Assigned Care Team (Doctors & Nurses)"
                          >
                            <FaHistory className="text-xs" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <div></div>

            <div className="flex items-center gap-2">
              <button
                onClick={handlePrev}
                disabled={currentPage === 1 || loading}
                className="px-3 py-1 bg-white text-xs rounded border border-slate-200 text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition-colors"
              >
                Prev
              </button>
              <span className="text-xs text-slate-600 font-medium px-1">
                {currentPage}
              </span>
              <button
                onClick={handleNext}
                disabled={list.length < pageSize || loading}
                className="px-3 py-1 bg-white text-xs rounded border border-slate-200 text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Care Team / Staff Assignment Modal */}
      {selectedAssignmentPatient && (
        <CareTeamModal
          patient={selectedAssignmentPatient}
          onClose={() => setSelectedAssignmentPatient(null)}
        />
      )}
    </Layouts>
  );
}
